import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { getCurrencySymbol } from '../utils/currencyUtil';
import { normalisePosExtra } from '../utils/posCustomizationUtil';
import { POS_CUSTOMIZATION_LABEL_MAX_LENGTH, POS_EXTRA_DESCRIPTION_MAX_LENGTH } from '../constants/posConst';
import '../css/Pos.css';

/**
 * Adds or edits ONE extra of a product — a paid add-on like "Pearl" that
 * staff can add to an item any number of times at the till.
 *
 * Like the option dialog, NOTHING IS WRITTEN FROM HERE: the extra goes back to
 * the product form and is saved with the product, so cancelling that form
 * leaves the product exactly as it was.
 *
 * Opens on top of the product form (stacked overlay) and closes on the × or
 * Cancel only (CLAUDE.md > Behavior).
 *
 * @param {object} props
 * @param {object|null} props.extra - the extra being edited, or null to add one
 * @param {Array<{id: string, label: string}>} props.siblings - the product's
 *   other extras, so two cannot share a label
 * @param {(extra: object) => void} props.onSave - receives the cleaned extra
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function PosExtraModal({ extra, siblings, onSave, onClose }) {
  const { config } = useConfig();
  const [label, setLabel] = useState(extra?.label || '');
  const [description, setDescription] = useState(extra?.description || '');
  // A string, so the field can be empty while typing; checked on save.
  const [price, setPrice] = useState(extra ? String(extra.price ?? '') : '');
  const [error, setError] = useState('');

  const handleSave = () => {
    setError('');
    try {
      onSave(normalisePosExtra({ id: extra?.id, label, description, price }, siblings));
    } catch (err) {
      setError(err.message || 'This extra could not be saved.');
    }
  };

  return createPortal(
    <div className="pos-modal-overlay pos-modal-overlay-stacked">
      <div className="pos-modal pos-modal-narrow" role="dialog" aria-modal="true" aria-label={extra ? 'Edit extra' : 'Add extra'}>
        <header className="pos-modal-head">
          <h3>{extra ? 'Edit extra' : 'Add extra'}</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          <label className="pos-field">
            <span>Label</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Pearl"
              maxLength={POS_CUSTOMIZATION_LABEL_MAX_LENGTH}
              autoFocus
            />
          </label>

          <label className="pos-field">
            <span>Description (optional)</span>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Extra scoop of tapioca pearls"
              maxLength={POS_EXTRA_DESCRIPTION_MAX_LENGTH}
            />
          </label>

          <label className="pos-field">
            <span>Add-on price ({getCurrencySymbol(config)})</span>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="10"
            />
            <span className="pos-theme-hint">
              Charged for each one added. Two extra pearls at this price adds it twice.
            </span>
          </label>

          {error && <p className="pos-error">{error}</p>}
        </div>

        <footer className="pos-modal-foot">
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={handleSave}>
            {extra ? 'Save extra' : 'Add extra'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosExtraModal;
